import { create } from 'zustand'

/**
 * Whether this reader is in Mitra's rollout: the engine's answer to
 * `GET /v1/access`, held for the page (`access.ts` asks for it).
 *
 * Kept apart from the check itself so `askClient.ts` can report a refusal
 * here without importing the module that calls it, and so the test setup can
 * state an answer without loading the engine client.
 *
 * - `pending`: not asked yet, or asked and not answered. Nothing Mitra is
 *   drawn: a launcher that showed now and went away a moment later would be
 *   worse than one that appeared a moment late.
 * - `enabled`: the engine let this reader in, now or on an earlier load
 *   whose answer this browser kept (`access.ts`).
 * - `disabled`: the engine said no, refused a call with `not_in_rollout`, or
 *   could not be reached on the first check with no earlier yes kept for
 *   this reader (`access.ts`).
 */
export type RolloutStatus = 'pending' | 'enabled' | 'disabled'

interface RolloutState {
  status: RolloutStatus
  /**
   * False while the last check could not reach the engine (offline, a
   * timeout, a 404 or a 5xx from it or the proxy in front of it): the chat
   * then says it cannot answer just now instead of failing silently. True
   * until a check says otherwise.
   */
  reachable: boolean
  /** Epoch ms after which the answer is asked for again; 0 asks at once. */
  staleAt: number
}

export const useRolloutStore = create<RolloutState>(() => ({
  status: 'pending',
  reachable: true,
  staleAt: 0,
}))

/** The `code` every reader endpoint answers 403 with for a reader outside the rollout. */
export const NOT_IN_ROLLOUT = 'not_in_rollout'

let refusalListener: (() => void) | null = null

/** `access.ts` registers the re-check a refusal asks for. One listener, replaced on each call. */
export function onNotInRollout(listener: () => void): void {
  refusalListener = listener
}

/** A reader call came back 403 `not_in_rollout`: Mitra is closed to this reader now. */
export function reportNotInRollout(): void {
  useRolloutStore.setState({ status: 'disabled' })
  refusalListener?.()
}

/** Whether a response is the engine's rollout refusal. Reads a clone, so the body stays the caller's. */
export async function isNotInRollout(response: Response): Promise<boolean> {
  if (response.status !== 403) return false
  try {
    const body: unknown = await response.clone().json()
    return (
      typeof body === 'object' &&
      body !== null &&
      (body as Record<string, unknown>)['code'] === NOT_IN_ROLLOUT
    )
  } catch {
    return false
  }
}
